import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { isSupabaseConfigured, supabase, requireSupabase } from '../lib/supabase';
import { AuthContext, type AuthContextValue, type AuthRole, type UserProfile } from './AuthContext';

function roleFromUserMetadata(user: User | null): AuthRole | null {
  const role = user?.user_metadata?.role;
  return role === 'admin' || role === 'customer' ? role : null;
}

async function loadProfile(user: User | null): Promise<UserProfile | null> {
  if (!user || !supabase) {
    return null;
  }

  const { data, error } = await supabase
    .from('profiles')
    .select('id,email,name,role,status,avatar_url')
    .eq('id', user.id)
    .maybeSingle();

  if (error) {
    throw new Error(`Unable to load profile: ${error.message}`);
  }

  if (!data) {
    return {
      id: user.id,
      email: user.email ?? '',
      name: user.user_metadata?.name ?? user.email ?? 'Customer',
      role: roleFromUserMetadata(user) ?? 'customer',
      status: 'active',
    };
  }

  return { ...data, avatarUrl: (data as any).avatar_url ?? null } as UserProfile;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [welcomeBack, setWelcomeBack] = useState(false);
  const user = session?.user ?? null;

  const refreshProfile = useCallback(async () => {
    const nextProfile = await loadProfile(session?.user ?? null);
    setProfile(nextProfile);
    return nextProfile;
  }, [session]);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    let alive = true;

    async function initializeAuth() {
      setLoading(true);
      const { data, error } = await supabase.auth.getSession();

      if (!alive) {
        return;
      }

      if (error) {
        setSession(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      setSession(data.session);

      try {
        setProfile(await loadProfile(data.session?.user ?? null));
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    initializeAuth();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setLoading(true);
      loadProfile(nextSession?.user ?? null)
        .then((nextProfile) => setProfile(nextProfile))
        .catch(() => setProfile(null))
        .finally(() => setLoading(false));
    });

    return () => {
      alive = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const clearWelcomeBack = useCallback(() => setWelcomeBack(false), []);

  const value = useMemo<AuthContextValue>(() => ({
    configured: isSupabaseConfigured,
    loading,
    session,
    user,
    profile,
    role: profile?.role ?? roleFromUserMetadata(user),
    welcomeBack,
    clearWelcomeBack,
    refreshProfile,
    signIn: async (email, password) => {
      if (!supabase) {
        throw new Error('Supabase is not configured.');
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (error) {
        throw error;
      }

      setSession(data.session);

      try {
        const nextProfile = await loadProfile(data.user);

        // Check if account was reactivated
        try {
          const sb = requireSupabase();
          const { data: status } = await sb.rpc('check_account_status');
          if (status === 'reactivated') {
            setWelcomeBack(true);
            const refreshed = await loadProfile(data.user);
            setProfile(refreshed);
            return refreshed;
          }
        } catch {
          // ignore check errors
        }

        setProfile(nextProfile);
        return nextProfile;
      } catch (err) {
        setProfile(null);
        throw new Error(
          err instanceof Error
            ? err.message
            : 'Signed in, but the user profile could not be loaded.'
        );
      }
    },
    signUp: async (name, email, password) => {
      if (!supabase) {
        throw new Error('Supabase is not configured.');
      }

      const { error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: {
            name,
            role: 'customer',
          },
        },
      });

      if (error) {
        throw error;
      }
    },
    signOut: async () => {
      if (!supabase) {
        return;
      }

      const { error } = await supabase.auth.signOut();

      if (error) {
        throw error;
      }

      setSession(null);
      setProfile(null);
    },
  }), [loading, profile, refreshProfile, session, user, welcomeBack, clearWelcomeBack]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
